import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Paper, Grid, Typography, TextField, Button, Select, MenuItem,
  FormControl, InputLabel, useMediaQuery, useTheme, Alert,
  Tooltip as MuiTooltip, IconButton,
} from '@mui/material';
import { InsertChartOutlined, CalendarToday, HelpOutline } from '@mui/icons-material';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { MenuInicial } from './MenuInicial';
import { API_BASE_URL, ANALISIS_GET } from '../services/apiConfig';
import TituloPagina from './utils/TituloPagina.jsx';
import Cargando from './utils/Cargando.jsx';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const toYmd = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const fmtMoneda = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtKg = (n) => `${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg`;
const fmtFechaCorta = (f) => {
  if (!f) return '';
  const d = new Date(String(f).length > 10 ? f : f + 'T12:00:00');
  if (isNaN(d)) return String(f);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
};

const PALETTE = ['#1565c0', '#2e7d32', '#e65100', '#6a1b9a', '#00838f', '#ad1457', '#9e9d24', '#4527a0'];

function StatCard({ label, value, color, ayuda }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        {ayuda && (
          <MuiTooltip title={ayuda} arrow placement="right">
            <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }}>
              <HelpOutline sx={{ fontSize: 14 }} />
            </IconButton>
          </MuiTooltip>
        )}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 800, color: color || 'text.primary' }}>{value}</Typography>
    </Paper>
  );
}

function ChartCard({ titulo, height = 280, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{titulo}</Typography>
      <ResponsiveContainer width="100%" height={height}>
        {children}
      </ResponsiveContainer>
    </Paper>
  );
}

function Analisis() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const colorSistema = localStorage.getItem('color_sistema') || '#071c44';

  const hoy = new Date();
  const primerDiaMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const [startDate, setStartDate] = useState(primerDiaMes);
  const [endDate, setEndDate] = useState(hoy);
  const [sucursalId, setSucursalId] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [datos, setDatos] = useState(null);

  const fetchDatos = useCallback(() => {
    setLoading(true);
    setError('');
    axios.get(API_BASE_URL + ANALISIS_GET, {
      params: { startDate: toYmd(startDate), endDate: toYmd(endDate), sucursal_id: sucursalId || undefined },
      headers: authHeader(),
    })
      .then((res) => setDatos(res.data?.data ?? null))
      .catch((e) => setError(e.response?.data?.message || 'Error al cargar los datos.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, sucursalId]);

  useEffect(() => { fetchDatos(); }, [fetchDatos]);

  const resumen = datos?.resumen ?? { cantidad_registros: 0, unidades: 0, peso_kg: 0, importe: 0 };
  const porDia = datos?.por_dia ?? [];
  const porProducto = datos?.por_producto ?? [];
  const porGrupo = datos?.por_grupo ?? [];
  const sucursales = datos?.sucursales ?? [];
  const sinDatos = !loading && !error && porDia.length === 0;

  const filtros = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
      <DatePicker
        selected={startDate} onChange={setStartDate} selectsStart
        startDate={startDate} endDate={endDate} dateFormat="dd/MM/yyyy"
        customInput={<TextField label="Desde" size="small" InputProps={{ startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} /> }} />}
      />
      <DatePicker
        selected={endDate} onChange={setEndDate} selectsEnd
        startDate={startDate} endDate={endDate} minDate={startDate} dateFormat="dd/MM/yyyy"
        customInput={<TextField label="Hasta" size="small" InputProps={{ startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} /> }} />}
      />
      {sucursales.length > 0 && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Sucursal</InputLabel>
          <Select label="Sucursal" value={sucursalId} onChange={(e) => setSucursalId(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            {sucursales.map((s) => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
          </Select>
        </FormControl>
      )}
      <Button onClick={fetchDatos} variant="contained">Buscar</Button>
    </Box>
  );

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={InsertChartOutlined}
          titulo="Análisis"
          subtitulo="Cantidad, peso e importe de lo registrado"
          ayuda="Solo cuenta lo registrado en el rango de fechas elegido, sin las ventas anuladas. Un franquiciado ve solo sus sucursales; el administrador ve todas."
        />

        {filtros}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Cargando inline />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><StatCard label="Registros" value={resumen.cantidad_registros} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Unidades" value={Number(resumen.unidades).toLocaleString('es-AR')} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Peso" value={fmtKg(resumen.peso_kg)} /></Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Importe" value={fmtMoneda(resumen.importe)} color="primary.main"
                ayuda="Bruto, antes de descuentos — para el neto realmente cobrado, ver Registros de productos."
              />
            </Grid>

            {sinDatos && (
              <Grid item xs={12}>
                <Alert severity="info">No hay registros en el período elegido.</Alert>
              </Grid>
            )}

            {!sinDatos && (
              <>
                <Grid item xs={12} md={8}>
                  <ChartCard titulo="Importe registrado por día">
                    <AreaChart data={porDia}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="fecha" tickFormatter={fmtFechaCorta} />
                      <YAxis tickFormatter={fmtMoneda} width={70} />
                      <Tooltip formatter={(v) => fmtMoneda(v)} labelFormatter={fmtFechaCorta} />
                      <Area type="monotone" dataKey="importe" name="Importe" stroke={colorSistema} fill={colorSistema} fillOpacity={0.25} />
                    </AreaChart>
                  </ChartCard>
                </Grid>
                <Grid item xs={12} md={4}>
                  <ChartCard titulo="Importe por grupo">
                    <PieChart>
                      <Pie data={porGrupo} dataKey="importe" nameKey="grupo_nombre" outerRadius={85} label={(d) => d.grupo_nombre}>
                        {porGrupo.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtMoneda(v)} />
                      <Legend />
                    </PieChart>
                  </ChartCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <ChartCard titulo="Unidades registradas por día">
                    <BarChart data={porDia}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="fecha" tickFormatter={fmtFechaCorta} />
                      <YAxis />
                      <Tooltip labelFormatter={fmtFechaCorta} />
                      <Bar dataKey="unidades" name="Unidades" fill={colorSistema} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ChartCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ChartCard titulo="Peso registrado por día (kg)">
                    <BarChart data={porDia}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="fecha" tickFormatter={fmtFechaCorta} />
                      <YAxis tickFormatter={(v) => fmtKg(v)} width={80} />
                      <Tooltip formatter={(v) => fmtKg(v)} labelFormatter={fmtFechaCorta} />
                      <Bar dataKey="peso_kg" name="Peso" fill="#2e7d32" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ChartCard>
                </Grid>

                <Grid item xs={12}>
                  <ChartCard titulo="Top productos por importe" height={Math.max(280, porProducto.length * 34)}>
                    <BarChart data={porProducto} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={fmtMoneda} />
                      <YAxis type="category" dataKey="descripcion" width={180} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v) => fmtMoneda(v)} />
                      <Bar dataKey="importe" name="Importe" fill={colorSistema} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ChartCard>
                </Grid>
              </>
            )}
          </Grid>
        )}
      </Box>
    </Container>
  );
}

export default Analisis;
